import { PH_CTA } from '../../utils/posthogEvents';
import {
  loginformImg,
  trustPilotRating,
  whiteCheck,
} from '../../assets/images';

import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';

import Image from 'next/image';

import { useRouter } from 'next/router';
import React, { useState } from 'react';
import {
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
} from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import ReCAPTCHA from 'react-google-recaptcha';

import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { useLogin } from '../../context/DocContext';

import Link from 'next/link';
import 'react-phone-input-2/lib/style.css';

const API_BASE = 'https://api-v2.24hrdoc.com';

const phoneRegExp = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
const SignupSchema = Yup.object().shape({
  fName: Yup.string()
    .required('Required')
    .min(2, 'Too Short!')
    .max(50, 'Too Long!'),
  lName: Yup.string()
    .required('Required')
    .min(2, 'Too Short!')
    .max(50, 'Too Long!'),
  email: Yup.string().email('Invalid email').required('Required'),
  phoneNumber: Yup.string()
    .matches(
      phoneRegExp,
      'Phone number is not valid,must be 10 digits only, no dashes or spaces'
    )
    .required('Phone number is required'),
  password: Yup.string()
    .min(8, 'Too Short!')
    .max(50, 'Too Long!')
    .required('Required'),
  confirm_password: Yup.string()
    .when('password', {
      is: (val) => (val && val.length > 0 ? true : false),
      then: Yup.string().oneOf(
        [Yup.ref('password')],
        'Your passwords do not match.'
      ),
    })
    .required('Please re-type your password.'),
});

export default function SignUp({ handleShowLogin, setShowSignUp }) {
  const router = useRouter();
  const login = useLogin();

  const [captcha, setCaptcha] = useState('');
  const [triggerSpinner, settriggerSpinner] = useState(false);
  const [step, setStep] = useState('form');

  const DISABLE_CAPTCHA = true;

  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  // NEW: store auth token returned by send-signup-code
  const [authToken, setAuthToken] = useState(null);

  // keep this to know which email we sent the code to
  const [userEmail, setUserEmail] = useState('');

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(SignupSchema),
  });

  // STEP 1: captcha check + send verification code email
  const onSubmit2 = async (data) => {
    if (!DISABLE_CAPTCHA && captcha === '') {
      toast.error('Please fill captcha');
      return;
    }

    try {
      settriggerSpinner(true);

      // if captcha is enabled, verify it server-side
      if (!DISABLE_CAPTCHA) {
        const captchaResponse = await axios.post('/api/checkCaptcha', {
          token: captcha,
        });

        if (!captchaResponse?.data?.success) {
          settriggerSpinner(false);
          toast.error('Please refresh the page and try again');
          return;
        }
      }

      // NEW: send signup code (Mailjet / backend) and store token
      const resp = await axios.post(`${API_BASE}/user/send-signup-code`, {
        email: data.email?.trim().toLowerCase(),
        phone: data.phoneNumber,
      });

      const token = resp?.data?.token;
      if (!token) {
        settriggerSpinner(false);
        toast.error('Could not get verification token. Please try again.');
        return;
      }

      setAuthToken(token);
      setUserEmail(data.email?.trim().toLowerCase());

      toast.success('Verification code sent to your email');
      setStep('verify');
    } catch (err) {
      toast.error(
        err?.response?.data?.message || 'Error!! Please contact support'
      );
    } finally {
      settriggerSpinner(false);
    }
  };

  // STEP 2: verify code + create account
  const handleVerifyCode = async () => {
    if (verifying) return;
    setVerifying(true);

    try {
      const values = getValues();

      if (!authToken) {
        toast.error('Missing verification token. Please resend code.');
        setVerifying(false);
        return;
      }

      const code = codeDigits.join('');
      if (!code || code.length < 6) {
        toast.error('Please enter the verification code.');
        setVerifying(false);
        return;
      }

      toast('Creating your account...');

      // NEW: create account using token + code (mirrors your other flow)
      const signupResp = await axios.post(`${API_BASE}/user/signup`, {
        first_name: values?.fName,
        last_name: values?.lName,
        email: userEmail,
        password: values?.password,
        number: values?.phoneNumber,

        token: authToken,
        code: code,
      });

      // treat 200/201 as success
      if (signupResp?.status === 200 || signupResp?.status === 201) {
        await login(userEmail, values?.password);
        toast.success('Account created successfully!');
        setStep('success');
      } else {
        toast.error(
          signupResp?.data?.message || 'Invalid code. Please try again.'
        );
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
          'Account creation failed. Please try again.'
      );
    }

    setVerifying(false);
  };

  // Resend code (also refresh token)
  const resendCode = async () => {
    try {
      const values = getValues();
      const resp = await axios.post(`${API_BASE}/user/send-signup-code`, {
        email: userEmail,
        phone: values?.phoneNumber,
      });

      const token = resp?.data?.token;
      if (token) setAuthToken(token);

      toast.success('Verification code resent');
      setCanResend(false);
      setResendTimer(30);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not resend code');
    }
  };

  // Timer effect for resend code
  React.useEffect(() => {
    let interval;
    if (step === 'verify' && resendTimer > 0 && !canResend) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, resendTimer, canResend]);

  // Handle digit input
  const handleDigitInput = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newDigits = [...codeDigits];
    newDigits[index] = value.slice(-1); // Only take last digit
    setCodeDigits(newDigits);

    // Auto-focus next input if digit is entered
    if (value && index < 5) {
      const nextInput = document.getElementById(`digit-${index + 1}`);
      nextInput?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      const prevInput = document.getElementById(`digit-${index - 1}`);
      prevInput?.focus();
    }
  };

  return (
    <main className='modalParent'>
      <Container fluid>
        <Row className=' modalStyle'>
          <h3 className='loginFormHeadingSmall'>
            Create <span className='gradientText'>an account</span>
          </h3>

          <Col xs={12} lg={6} className=' d-flex  h-100 '>
            <div className='flex-grow-1 position-relative modalImageHeight'>
              <Image
                src={loginformImg}
                layout='fill'
                objectFit='cover'
                quality={80}
                alt='sign-up-image'
                style={{ borderRadius: '1.25rem' }}
              />
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  zIndex: '2',
                  padding: '1.25rem 1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                }}
              >
                <div className='flex-grow-1'>
                  {ImgOverlay.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        columnGap: '6px',
                      }}
                    >
                      <Image
                        src={whiteCheck}
                        width={18}
                        height={18}
                        alt='tick-mark'
                      />
                      <span className='formImgOverlayText'>{item.content}</span>
                    </div>
                  ))}
                </div>
                <div className='trustPilotRatingImgDiv'>
                  <Image
                    src={trustPilotRating}
                    layout='fill'
                    objectFit='cover'
                    alt='trust-pilot-rating'
                    quality={80}
                    style={{ borderRadius: '0.75rem' }}
                  />
                </div>
              </div>
            </div>
          </Col>

          <Col xs={12} lg={6}>
            <div className='loginFormContainer '>
              {step === 'success' ? (
                <div className='signedUpArea'>
                  <h3>
                    Your account was successfully created. Welcome to the{' '}
                    <span className='gradientText'>24hrdoc</span> family!
                  </h3>
                  <Link href='/dashboard'>
                    <button>Go to Dashboard</button>
                  </Link>
                  <p onClick={() => setShowSignUp(false)}>Remain on the page</p>
                </div>
              ) : step === 'verify' ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2rem',
                  }}
                >
                  <div
                    style={{
                      textAlign: 'center',
                      maxWidth: 500,
                      width: '100%',
                    }}
                  >
                    <h2
                      className='flowHeading'
                      style={{
                        fontSize: '28px',
                        fontWeight: 600,
                        marginBottom: '1rem',
                        marginTop: 0,
                      }}
                    >
                      Verify your{' '}
                      <span style={{ color: '#20B2AA' }}>email</span>
                    </h2>

                    <p
                      style={{
                        color: '#666',
                        fontSize: '14px',
                        marginBottom: '2rem',
                        lineHeight: 1.6,
                      }}
                    >
                      We use this to protect your account and make sure your
                      provider can reach you.
                    </p>

                    <div style={{ marginBottom: '2rem' }}>
                      <h3
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          marginBottom: '0.5rem',
                          color: '#000',
                        }}
                      >
                        Enter the 6-digit code
                      </h3>
                      <p
                        style={{
                          fontSize: '13px',
                          color: '#999',
                          margin: 0,
                        }}
                      >
                        A verification code was sent to{' '}
                        {/* ph-mask: masked in session replay, see
                            utils/posthogClient.js. Patient still sees it. */}
                        <strong className='ph-mask'>
                          {getValues('email')}
                        </strong>
                        . Can&apos;t find it? Check your spam or junk folder.
                      </p>
                    </div>

                    {/* 6-digit code inputs */}
                    <div
                      style={{
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'center',
                        marginBottom: '2rem',
                        marginTop: '1.5rem',
                      }}
                    >
                      {codeDigits.map((digit, index) => (
                        <input
                          key={index}
                          id={`digit-${index}`}
                          type='text'
                          inputMode='numeric'
                          maxLength='1'
                          value={digit}
                          onChange={(e) =>
                            handleDigitInput(index, e.target.value)
                          }
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          style={{
                            width: '50px',
                            height: '50px',
                            fontSize: '24px',
                            fontWeight: 600,
                            textAlign: 'center',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            outline: 'none',
                            boxSizing: 'border-box',
                            backgroundColor: '#f9f9f9',
                            transition: 'border-color 0.2s',
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#20B2AA';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                          }}
                        />
                      ))}
                    </div>

                    {/* Verify button */}
                    <button
                      onClick={handleVerifyCode}
                      disabled={verifying || codeDigits.join('').length !== 6}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: verifying ? '#999' : '#000',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '30px',
                        fontSize: '16px',
                        fontWeight: 600,
                        cursor: verifying ? 'not-allowed' : 'pointer',
                        marginBottom: '1rem',
                        // opacity: codeDigits.join('').length !== 6 ? 0.5 : 1,
                      }}
                      type='button'
                    >
                      {verifying ? 'Verifying...' : 'Verify Code'}
                    </button>

                    {/* Security notice */}
                    <p
                      style={{
                        fontSize: '12px',
                        color: '#999',
                        marginBottom: '2rem',
                      }}
                    >
                      No spam. We only use this for your care and account
                      security
                    </p>

                    {/* Resend code section */}
                    <div
                      style={{
                        marginBottom: '1.5rem',
                        borderTop: '1px solid #e0e0e0',
                        paddingTop: '1.5rem',
                      }}
                    >
                      <p
                        style={{
                          fontSize: '13px',
                          color: '#666',
                          marginBottom: '0.5rem',
                        }}
                      >
                        Didn&apos;t receive an email?
                      </p>
                      <button
                        onClick={resendCode}
                        disabled={!canResend}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: canResend ? '#20B2AA' : '#ccc',
                          cursor: canResend ? 'pointer' : 'not-allowed',
                          fontSize: '14px',
                          fontWeight: 600,
                          textDecoration: 'underline',
                          padding: 0,
                        }}
                        type='button'
                      >
                        Resend code{canResend ? '' : ` in ${resendTimer}s`}
                      </button>
                    </div>

                    {/* Change email link */}
                    <button
                      onClick={() => setStep('form')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#20B2AA',
                        cursor: 'pointer',
                        fontSize: '14px',
                        textDecoration: 'underline',
                        marginBottom: '2rem',
                        padding: 0,
                      }}
                      type='button'
                    >
                      Change email
                    </button>

                    {/* Go back link */}
                    {/* <button
                      onClick={() => setShowSignUp(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#333',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 600,
                        padding: 0,
                      }}
                      type='button'
                    >
                      Go back
                    </button> */}
                  </div>
                </div>
              ) : (
                <Container className=' containerSignup '>
                  <Row>
                    <Col className='px-0'>
                      <Form
                        onSubmit={handleSubmit(onSubmit2)}
                        className='theLoginForm tw-gap-y-1'
                      >
                        <h3 className='loginTitle'>
                          Create{' '}
                          <span className='gradientText'> an account</span>
                        </h3>

                        <Row className='px-0 mb-3'>
                          <Col xs={12} md={6}>
                            <p className='formInputLabel'>First Name</p>
                            <InputGroup>
                              <Form.Control
                                placeholder='First Name'
                                {...register('fName')}
                                className='formInputLogin'
                              />
                            </InputGroup>
                            {errors.fName && (
                              <Form.Text className={'text-muted error'}>
                                {errors.fName.message}
                              </Form.Text>
                            )}
                          </Col>

                          <Col xs={12} md={6}>
                            <p className='formInputLabel'>Last Name</p>
                            <InputGroup>
                              <Form.Control
                                placeholder='Last Name'
                                {...register('lName')}
                                className='formInputLogin'
                              />
                            </InputGroup>
                            {errors.lName && (
                              <Form.Text className={'text-muted error'}>
                                {errors.lName.message}
                              </Form.Text>
                            )}
                          </Col>
                        </Row>

                        <p className='formInputLabel'>Email</p>
                        <InputGroup>
                          <Form.Control
                            placeholder='johnsmith@gmail.com'
                            {...register('email')}
                            className='formInputLogin mb-3'
                          />
                        </InputGroup>
                        {errors.email && (
                          <Form.Text className={'text-muted error'}>
                            {errors.email.message}
                          </Form.Text>
                        )}

                        <p className='formInputLabel'>Phone Number</p>
                        <InputGroup>
                          <Form.Control
                            placeholder='8312345678'
                            {...register('phoneNumber')}
                            className='formInputLogin mb-3'
                          />
                        </InputGroup>
                        {errors.phoneNumber && (
                          <Form.Text className={'text-muted error'}>
                            {errors.phoneNumber.message}
                          </Form.Text>
                        )}

                        <p className='formInputLabel'>Password</p>
                        <InputGroup>
                          <Form.Control
                            type='password'
                            placeholder='********'
                            {...register('password')}
                            className='formInputLogin mb-3'
                          />
                        </InputGroup>
                        {errors.password && (
                          <Form.Text className={'text-muted error'}>
                            {errors.password.message}
                          </Form.Text>
                        )}

                        <p className='formInputLabel'>Confirm Password</p>
                        <InputGroup>
                          <Form.Control
                            type='password'
                            placeholder='********'
                            {...register('confirm_password')}
                            className='formInputLogin mb-3'
                          />
                        </InputGroup>
                        {errors.confirm_password && (
                          <Form.Text className={'text-muted error'}>
                            {errors.confirm_password.message}
                          </Form.Text>
                        )}

                        {!DISABLE_CAPTCHA && (
                          <div
                            style={{
                              paddingTop: '1rem',
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            <ReCAPTCHA
                              sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY}
                              onChange={setCaptcha}
                            />
                          </div>
                        )}

                        <button
                          type='submit'
                          className='loginButtonForm d-flex justify-content-center align-items-center'
                        >
                          Create an account{' '}
                          {triggerSpinner ? (
                            <Spinner
                              variant='light'
                              style={{
                                width: '1.5rem',
                                height: '1.5rem',
                                marginLeft: '.75rem',
                              }}
                            />
                          ) : null}
                        </button>

                        <p className='text-center' style={{ fontSize: 12 }}>
                          Already Have An Account?{' '}
                          <button
                            style={{ border: 'none', fontWeight: 600 }}
                            onClick={() =>
                              handleShowLogin(PH_CTA.ALREADY_HAVE_ACCOUNT)
                            }
                            type='button'
                          >
                            Login
                          </button>
                        </p>
                      </Form>
                    </Col>
                  </Row>
                </Container>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </main>
  );
}

const ImgOverlay = [
  { id: 1, content: `Save your progress` },
  { id: 2, content: `Easy Refills` },
  { id: 3, content: `Track your health` },
];
